import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetOneResult } from 'core/types';
import { corporatePartService } from 'corporate/services';
import {
  ICorporatePart,
  ICorporatePartCostListItem,
  ICorporatePartCostListPayload,
  ICorporatePartExtraData,
  ICorporatePartPriceListItem,
  ICorporatePartPriceListPayload,
  ICorporatePartRequestPayload,
} from 'corporate/types';

export const useGetCorporatePart = (
  requestPayload?: ICorporatePartRequestPayload,
  enabled = false
): GetOneResult<Partial<ICorporatePart>, ICorporatePartExtraData> => {
  const { data, isError, isLoading, refetch } = useQuery(
    ['corporatePart', requestPayload],
    ({ signal }) => corporatePartService.getCorporatePart(requestPayload, signal),
    { enabled }
  );

  return {
    record: data?.record,
    extraData: data?.extraData,
    isError,
    isLoading,
    refetch,
  };
};

export const useUpsertCorporatePart = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messageCompanyPartUpdateSuccess') },
    loading: { message: t('messageCompanyPartUpdateInProgress') },
    error: { title: t('errorTitleCompanyPartUpdate') },
    position: NotificationPosition.FORM,
    invalidateQueries: ['corporatePart'],
  });

  const { mutateAsync } = useMutation(corporatePartService.upsertCorporatePart, {
    ...errors,
  });

  return { upsertCorporatePart: mutateAsync };
};

export const useUpdateCorporatePartPricingType = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messageCompanyPartUpdateSuccess') },
    loading: { message: t('messageCompanyPartUpdateInProgress') },
    error: { title: t('errorTitleCompanyPartUpdate') },
    position,
    invalidateQueries: ['corporatePart', 'corporatePartPriceList'],
  });

  const { mutateAsync } = useMutation(corporatePartService.updateCorporatePartPricingType, {
    ...errors,
  });

  return { updatePricingType: mutateAsync };
};

export const useGetCorporatePartCosts = (
  requestPayload: ICorporatePartCostListPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<ICorporatePartCostListItem>(
    corporatePartService.getCorporatePartCosts,
    'corporatePartCostList',
    requestPayload,
    options
  );

export const useGetCorporatePartPrices = (
  requestPayload: ICorporatePartPriceListPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<ICorporatePartPriceListItem>(
    corporatePartService.getCorporatePartPrices,
    'corporatePartPriceList',
    requestPayload,
    options
  );
